import React from 'react';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import {
  parseProgramChecklist,
  resolveGrantProgramChecklist,
} from '../utils/programChecklistUtils.js';

function countSteps(tasks = []) {
  let total = 0;
  for (const task of tasks) {
    const subtasks = task.subtasks || [];
    total += subtasks.length > 0 ? subtasks.length : 1;
  }
  return total;
}

export default function ProgramChecklistPreview({
  grant,
  programChecklist = null,
  compact = false,
  emptyMessage = 'No program checklist on this grant yet.',
}) {
  const parsed =
    programChecklist ||
    (grant
      ? resolveGrantProgramChecklist(
          grant.fields?.ProgramChecklist,
          grant.fields?.ToDone
        )
      : null);

  if (!parsed?.tasks?.length) {
    return (
      <p className="text-xs text-gray-400">{emptyMessage}</p>
    );
  }

  const stepCount = countSteps(parsed.tasks);
  const requiredCount = parsed.requiredInfoFields?.filter((f) => f.required)
    .length;
  const previewTasks = parsed.tasks.slice(0, compact ? 3 : 5);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-900">
          <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 text-gray-500" />
          {parsed.checklistLabel || 'Program checklist'}
        </span>
        <span className="text-[0.65rem] font-medium text-gray-500">
          {stepCount} steps
          {requiredCount > 0 && ` · ${requiredCount} required`}
        </span>
      </div>

      {parsed.source && (
        <p className="text-[0.65rem] text-gray-400 capitalize">
          Source: {parsed.source}
        </p>
      )}

      <ul className="space-y-1">
        {previewTasks.map((task) => (
          <li key={task.id} className="text-xs text-gray-600">
            <span className="font-medium text-gray-700">{task.name}</span>
            {!compact && task.subtasks?.length > 0 && (
              <ul className="mt-0.5 space-y-0.5 pl-3 text-gray-500">
                {task.subtasks.slice(0, 3).map((sub) => (
                  <li key={sub.id}>· {sub.name}</li>
                ))}
                {task.subtasks.length > 3 && (
                  <li className="text-gray-400">
                    +{task.subtasks.length - 3} more
                  </li>
                )}
              </ul>
            )}
          </li>
        ))}
        {parsed.tasks.length > previewTasks.length && (
          <li className="text-xs text-gray-400">
            +{parsed.tasks.length - previewTasks.length} more phases
          </li>
        )}
      </ul>

      {parsed.requiredInfoFields?.length > 0 && (
        <div className="border-t border-gray-100 pt-2">
          <p className="text-[0.65rem] font-medium uppercase tracking-wide text-gray-400">
            Required info
          </p>
          <ul className="mt-1 space-y-0.5">
            {parsed.requiredInfoFields.slice(0, compact ? 3 : 5).map((field) => (
              <li key={field.id} className="text-xs text-gray-600">
                {field.label}
                {field.required && (
                  <span className="ml-1 text-gray-400">· required</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export { parseProgramChecklist };
